import { useEffect, useRef, useState } from "react";
import SubscribeForm from "./components/SubscribeForm";
import { useSEO } from "./seo";

function Countdown() {
  const target = process.env.REACT_APP_RELEASE_DATE;
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!target) return null;
  const diff = new Date(target).getTime() - now;
  if (!Number.isFinite(diff) || diff <= 0) return null;

  const tiles = [
    [Math.floor(diff / 86400000), "Days"],
    [Math.floor((diff / 3600000) % 24), "Hours"],
    [Math.floor((diff / 60000) % 60), "Min"],
    [Math.floor((diff / 1000) % 60), "Sec"],
  ];

  return (
    <div className="mt-12 flex items-center justify-center gap-3" data-testid="countdown">
      {tiles.map(([v, label]) => (
        <div
          key={label}
          className="w-20 rounded-xl border-2 border-bone/50 bg-ink px-2 py-4 text-center"
        >
          <div className="font-georgia text-3xl tracking-tight text-bone">
            {String(v).padStart(2, "0")}
          </div>
          <div className="mt-1 font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-bone/50">
            {label}
          </div>
        </div>
      ))}
    </div>
  );
}

function PixelPour() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const W = (canvas.width = 320);
    const H = (canvas.height = 160);

    const off = document.createElement("canvas");
    off.width = W;
    off.height = H;
    const octx = off.getContext("2d");
    octx.font = "84px Georgia";
    octx.textAlign = "center";
    octx.textBaseline = "middle";
    octx.fillStyle = "#F4F1EC";
    octx.fillText("nt.", W / 2, H / 2);

    const gap = 5;
    const particles = [];
    const data = octx.getImageData(0, 0, W, H).data;
    for (let y = 0; y < H; y += gap) {
      for (let x = 0; x < W; x += gap) {
        if (data[(y * W + x) * 4 + 3] > 128) {
          particles.push({
            tx: x,
            ty: y,
            col: Math.round(x / gap),
            spoutX: W / 2 + (Math.random() - 0.5) * 50,
            jitter: (Math.random() - 0.5) * 0.18,
            dissolveRand: Math.random(),
          });
        }
      }
    }

    const columns = new Map();
    for (const particle of particles) {
      if (!columns.has(particle.col)) columns.set(particle.col, []);
      columns.get(particle.col).push(particle);
    }
    let maxColumnCount = 1;
    for (const grains of columns.values()) {
      grains.sort((a, b) => b.ty - a.ty);
      grains.forEach((particle, i) => {
        particle.fillIndex = i;
      });
      maxColumnCount = Math.max(maxColumnCount, grains.length);
    }

    // grains pour, pile up as pixel letters, hold, then dissolve pixel-by-pixel and restart
    const PHASES = { stack: 2200, hold: 600, dissolve: 1200, pause: 400 };
    const T_STACK = PHASES.stack;
    const T_HOLD = T_STACK + PHASES.hold;
    const T_DISSOLVE = T_HOLD + PHASES.dissolve;
    const TOTAL = T_DISSOLVE + PHASES.pause;

    const easeIn = (t) => t * t;
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const clamp01 = (v) => Math.max(0, Math.min(1, v));
    const FALL_DIST = 220;
    const GRAIN_FALL_FRAC = 0.28;
    const STAGGER = 0.4;

    let raf;
    const drawPixelLetters = (alpha) => {
      if (alpha <= 0.01) return;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = "#F4F1EC";
      for (const particle of particles) {
        ctx.fillRect(particle.tx, particle.ty, gap - 1, gap - 1);
      }
      ctx.globalAlpha = 1;
    };
    const draw = (now) => {
      const t = now % TOTAL;
      ctx.clearRect(0, 0, W, H);
      if (t < T_STACK) {
        const p = t / PHASES.stack;
        for (const particle of particles) {
          let start =
            (particle.fillIndex / maxColumnCount) * (1 - GRAIN_FALL_FRAC) +
            particle.jitter * (1 - GRAIN_FALL_FRAC);
          start = clamp01(start);
          const local = clamp01((p - start) / GRAIN_FALL_FRAC);
          if (local <= 0) continue;
          const fall = easeIn(local);
          const settle = easeOut(local);
          const y = particle.ty - FALL_DIST + FALL_DIST * fall;
          const x = particle.spoutX + (particle.tx - particle.spoutX) * settle;
          ctx.fillStyle = "#F4F1EC";
          ctx.fillRect(x, y, gap - 1, gap - 1);
        }
      } else if (t < T_HOLD) {
        drawPixelLetters(1);
      } else if (t < T_DISSOLVE) {
        const p = (t - T_HOLD) / PHASES.dissolve;
        for (const particle of particles) {
          const delay = particle.dissolveRand * STAGGER;
          const local = clamp01((p - delay) / (1 - delay));
          const eased = easeOut(local);
          const alpha = 1 - eased;
          const size = (gap - 1) * (1 - 0.5 * eased);
          const offset = (gap - 1 - size) / 2;
          if (alpha > 0.01) {
            ctx.globalAlpha = alpha;
            ctx.fillStyle = "#F4F1EC";
            ctx.fillRect(particle.tx + offset, particle.ty + offset, size, size);
          }
        }
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <canvas
      ref={ref}
      width={320}
      height={160}
      data-testid="soon-canvas"
      aria-label="nt. — loading"
    />
  );
}

export default function ComingSoon() {
  useSEO({
    title: "nailtape™ — A huge update is coming",
    description:
      "The updated version of nailtape™ is on track — very soon. Join the list and don't miss a thing.",
    path: "/",
  });
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center bg-coal px-6 py-20 text-center"
      data-testid="coming-soon-page"
    >
      <PixelPour />
      <h1 className="mt-12 max-w-3xl font-georgia text-4xl leading-[1.08] tracking-tight text-bone md:text-6xl">
        nailtape<span className="align-super text-[0.4em] tracking-normal">™</span>{" "}
        is soon to release its new update.
      </h1>
      <p className="mt-7 max-w-xl text-base leading-relaxed text-bone/70 md:text-lg">
        We know how long you've waited. We've seen your questions and your
        sign-ups, and we are on track to release the updated version of
        nailtape™ <strong className="font-bold text-bone">very soon</strong>.
        Stay with us and drop your email below so you don't miss a thing.
      </p>
      <Countdown />
      <div className="mt-10 w-full max-w-md">
        <SubscribeForm source="update_waitlist" />
      </div>
      <p className="mt-16 font-sans text-[11px] font-bold uppercase tracking-[0.2em] text-bone/40">
        nailtape™ — the standard for the modern press-on business
      </p>
      <div
        className="mt-5 flex items-center gap-6"
        data-testid="soon-legal-links"
      >
        <a
          href="/privacy"
          data-testid="soon-privacy-link"
          className="font-display text-sm font-extrabold uppercase tracking-tight text-pastel-pink underline decoration-2 underline-offset-4 transition-colors duration-200 hover:text-bone"
        >
          Privacy
        </a>
        <a
          href="/terms"
          data-testid="soon-terms-link"
          className="font-display text-sm font-extrabold uppercase tracking-tight text-pastel-pink underline decoration-2 underline-offset-4 transition-colors duration-200 hover:text-bone"
        >
          Terms
        </a>
      </div>
    </div>
  );
}
